import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Navbar, Nav, Container, Offcanvas } from 'react-bootstrap';
import './NavigationBar.css'; // Import custom styles

import {
  getUpcomingEvent,
  getAllMenuItems,
  getNowEntries,
  findNowIndex,
  NOW_PLACEMENT_MAP,
} from '../api/api';
import NowOverlay from './NowOverlay';

// Import icons using object approach
import { icons } from '../assets/icons';

// Import logo separately
import logo from '../assets/images/rlp-logo.png';

import versionInfo from '../version.json';

// Dreistellig, z. B. "007"
const VERSION_LABEL = String(versionInfo.version).padStart(3, '0');

const NavigationBar = () => {
  const [show, setShow] = useState(false);
  const [eventData, setEventData] = useState(null);
  const [nowEntries, setNowEntries] = useState([]);
  const [nowStartIndex, setNowStartIndex] = useState(-1);
  const [showNowOverlay, setShowNowOverlay] = useState(false);
  const [menuIcons, setMenuIcons] = useState({
    art: icons.art,
    film: icons.film,
    architecture: icons.architecture,
    literature: icons.literature,
    history: icons.history
  });

  useEffect(() => {
    async function fetchEventData() {
      try {
        const data = await getUpcomingEvent(); // Fetch event data from Strapi API
        if (data) {
          setEventData(data.data);
        }
      } catch (error) {
        console.error('Error fetching event data:', error);
      }
    }

    async function fetchLatestNow() {
      try {
        // Der Event-Knopf oeffnet dasselbe Overlay wie die Karte: Start beim
        // neuesten Beitrag mit Anzeigeort "Karte", Blaettern durch alle
        const data = await getNowEntries();
        const list = data?.data || [];
        setNowEntries(list);
        setNowStartIndex(findNowIndex(list, NOW_PLACEMENT_MAP));
      } catch (error) {
        console.error('Error fetching now entries:', error);
      }
    }

    async function fetchMenuIcons() {
      try {
        const menuData = await getAllMenuItems();
        
        const updatedIcons = {
          art: icons.art,
          film: icons.film,
          architecture: icons.architecture,
          literature: icons.literature,
          history: icons.history
        };        // Check if each menu item has an icon uploaded and update accordingly
        if (menuData.art?.data?.Icon?.formats?.thumbnail?.url) {
          updatedIcons.art = `${process.env.REACT_APP_STRAPI_API_URL}${menuData.art.data.Icon.formats.thumbnail.url}`;
        }

        if (menuData.film?.data?.Icon?.formats?.thumbnail?.url) {
          updatedIcons.film = `${process.env.REACT_APP_STRAPI_API_URL}${menuData.film.data.Icon.formats.thumbnail.url}`;
        }

        if (menuData.architecture?.data?.Icon?.formats?.thumbnail?.url) {
          updatedIcons.architecture = `${process.env.REACT_APP_STRAPI_API_URL}${menuData.architecture.data.Icon.formats.thumbnail.url}`;
        }

        if (menuData.literature?.data?.Icon?.formats?.thumbnail?.url) {
          updatedIcons.literature = `${process.env.REACT_APP_STRAPI_API_URL}${menuData.literature.data.Icon.formats.thumbnail.url}`;
        }

        // History bleibt vorerst statisch - es gibt (noch) keinen Strapi-Single-Type dafuer

        setMenuIcons(updatedIcons);
      } catch (error) {
        console.error('Error fetching menu icons:', error);
        // Keep using static icons as fallback
      }
    }

    fetchEventData();
    fetchLatestNow();
    fetchMenuIcons();
  }, []);

  const pushEvent = (eventName, params = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...params });
  };

  const handleClose = () => {
    setShow(false);
    pushEvent('menu_close');
  };
  const handleShow = () => {
    setShow(true);
    pushEvent('menu_open');
  };

  const handleEventClick = (e) => {
    e.preventDefault();
    pushEvent('nav_click', { nav_label: 'event_header' });
    if (nowStartIndex >= 0) {
      setShowNowOverlay(true);
    } else {
      window.location.href = '/gallery-weekend';
    }
  };

  // In Strapi einstellbare Groesse der Event-Ueberschrift (Feld "titleSize")
  const TITLE_SIZE_PX = {
    small: 14,
    medium: 18,
    large: 24,
    xlarge: 32,
  };
  const titleFontSize = TITLE_SIZE_PX[eventData?.titleSize] || TITLE_SIZE_PX.medium;

  // Common Menu Content used for both mobile and desktop
  const MenuContent = () => (
    <Nav className="flex-column" variant="tabs">
      <Nav.Item className="nav-item-top text-center">
        <Nav.Link href="/gallery-weekend" onClick={handleEventClick}>
          <div className="event-header-nav-item" style={{ fontSize: `${titleFontSize}px` }}>
            {eventData ? eventData.Title : 'Loading...'}
          </div>
          <div className="event-body-nav-item">
            {eventData ? eventData.Description : 'Loading...'}
          </div>
        </Nav.Link>
      </Nav.Item>      {[
        { href: "/map/Art", icon: menuIcons.art, label: "Art" },
        { href: "/map/Film & Theater", icon: menuIcons.film, label: "Film & Theater" },
        { href: "/map/Architecture", icon: menuIcons.architecture, label: "Architecture" },
        { href: "/map/Literature", icon: menuIcons.literature, label: "Literature" },
        { href: "/historie", icon: menuIcons.history, label: "Historie" }
      ].map((item, index) => (
        <Nav.Item key={index}>
          <Nav.Link
            className="position-relative d-flex justify-content-between align-items-center"
            href={item.href}
            onClick={() => pushEvent('nav_click', { nav_label: item.label })}
          >
            <span className="item-label" style={{ fontSize: '1.1rem', paddingRight: '40px' }}>
              {item.label}
            </span>
            <img
              src={item.icon}
              alt={item.label}
              className="icon-style"
              style={{ position: 'absolute', right: '10px' }}
            />
          </Nav.Link>
        </Nav.Item>
      ))}
      <Nav.Item>
        <Nav.Link
          className="position-relative d-flex align-items-center"
          style={{ minHeight: '44px' }}
          href="/mehr"
          title="Mehr"
          aria-label="Mehr"
          onClick={() => pushEvent('nav_click', { nav_label: 'Mehr' })}
        >
          {/* Steigt bei jedem Build, siehe scripts/bump-version.js */}
          <span className="nav-version">
            Version #{VERSION_LABEL}
            {versionInfo.date && <> · {versionInfo.date}</>}
          </span>
          {/* "i" im Kreis, im Stil der Kategorie-Icons, mittig unter ihnen */}
          <svg className="nav-info-icon" width="32" height="32" viewBox="0 0 38 38" aria-hidden="true">
            <circle cx="19" cy="19" r="16.5" fill="#fff" stroke="#000" strokeWidth="3" />
            <circle cx="19" cy="11.5" r="2.6" fill="#000" />
            <rect x="16.6" y="16" width="4.8" height="13" rx="1.6" fill="#000" />
          </svg>
        </Nav.Link>
      </Nav.Item>
    </Nav>
  );

  return (
    <>
      {/* Mobile Navbar with hamburger menu and search bar */}
      <Navbar expand={false} className="d-lg-none floating-navbar"   
        style={{
          position: 'fixed',
          top: 0,
          width: '100%',
          zIndex: 1000,
          padding: '16px',
          backgroundColor: "transparent"
        }}>
        <Container className="d-flex justify-content-between align-items-center flex-row-reverse">
          {/* Menu/Close icon */}
          <span onClick={handleShow} style={{ cursor: 'pointer' }}>
            <img 
              src={show ? icons.close : icons.menu}
              alt={show ? "Close" : "Menu"} 
              style={{ width: '50px', height: '50px' }}
            />
          </span>
          {/* Search bar */}
          {/* <div className="search-bar d-flex align-items-center justify-content-between">            <span className="search-icon">
              <img 
                src={icons.loop} 
                alt="Search" 
                style={{ width: '30px', height: '30px' }}
              />
            </span>
            <input type="text" placeholder="" className="search-input" />
          </div> */}
        </Container>
      </Navbar>

      {/* Mobile Offcanvas menu */}
      <Offcanvas
        show={show}
        onHide={handleClose}
        placement="end"
        className="bg-transparent d-lg-none"
        style={{ 
          marginTop: '80px',
          width: '350px'
        }}
      >
        <Offcanvas.Body style={{ padding: '1rem' }}>
          <MenuContent />
        </Offcanvas.Body>
      </Offcanvas>

      {/* Desktop permanent sidebar menu */}
      <div className="d-none d-lg-block" 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100vh',
          width: '350px',
          zIndex: 1000,
          backgroundColor: 'transparent',
          paddingTop: '20px',
          overflow: 'auto'
        }}>
        <div style={{ padding: '1rem' }}>
          <MenuContent />
        </div>
      </div>
      
      {/* Full-width footer for desktop */}
      <div className="d-none d-lg-block" 
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100%',
          zIndex: 1000,
          backgroundColor: 'white',
          padding: '15px 30px',
          borderTop: '1px solid #eee'
        }}>
        <Container fluid>
          <div className="d-flex justify-content-between align-items-center">
            {/* Left side with "Sponsored by" text */}
            <div className="d-flex align-items-center">
              <span style={{ fontSize: '0.9rem' }}>Sponsored by</span>
            </div>
            
            {/* Center with logo */}
            <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
              <a href="https://www.rosa-luxemburg-platz.net/" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'black' }}>
                <div style={{ fontFamily: 'serif', fontSize: '1.2rem', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                  <img src={logo} alt="Kunstverein am Rosa-Luxemburg-Platz" height="30" />
                  {' '}Kunstverein am Rosa-Luxemburg-Platz
                </div>
              </a>
            </div>
            
            {/* Right side with links */}
            <div>
              <Link to="/impressum" className="me-3" style={{ textDecoration: 'none', color: 'black', fontSize: '0.9rem' }}>
                Impressum
              </Link>
              <Link to="/datenschutz" style={{ textDecoration: 'none', color: 'black', fontSize: '0.9rem' }}>
                Datenschutz
              </Link>
            </div>
          </div>
        </Container>
      </div>

      <NowOverlay
        entries={showNowOverlay ? nowEntries : []}
        startIndex={Math.max(nowStartIndex, 0)}
        onClose={() => setShowNowOverlay(false)}
      />
    </>
  );
};

export default NavigationBar;